import { useEffect, useId, useRef, useState } from 'react';
import type { CancelLineRequest, OrderCancelType, ReceivedGoods } from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { LockIcon } from '@/pages/fulfillment/components/CancelLockIcon';
import {
  cancelGoodsAnswered,
  cancelGoodsDispositions,
  type CancelGoodsDisposition,
  describeCancelGoods,
  type CancelGoodsChoice,
  type CancelGoodsLine,
} from '@/utils/cancelGoods';
import { describeCancelLock, type CancelLock } from '@/utils/fulfillmentCancelLock';
import './cancelDialog.css';

interface CancelRequest {
  title: string;
  confirmLabel: string;
  maxReasonLength: number;
  /** Offer keep / write-off; only an order or line with received goods that can be written off has the choice. */
  showReceivedGoods?: boolean;
  /** Ask the cancel reason, whose group is the cancel type (SSD-7450). */
  askCancelType?: boolean;
  /** The order's cancel lock: shown, and on a locked order the dialog asks why it is cancelled anyway (SSD-7643). */
  lock?: CancelLock | null;
  /** The lines the cancel cancels, to say or ask what happens to each one's goods (SSD-7643); none skips the block. */
  goodsLines?: readonly CancelGoodsLine[];
}

/** What the dialog answers: the reason, the stock choice when it was offered, and the cancel type when it was asked. */
export interface CancelAnswer extends CancelLineRequest {
  cancelType?: OrderCancelType;
  /** Why a locked order is cancelled anyway (SSD-7643); set only on a locked order. The server posts it as a comment. */
  lockOverrideReason?: string;
  /** What to do with each line's goods (SSD-7643); set when any line needs action. The server posts it as a comment. */
  goodsNote?: string;
  /** Structured per-line disposition; its stock effect is owned by CancelGoodsDisposition. */
  lines?: CancelGoodsDisposition[];
}

interface Pending extends CancelRequest {
  id: number;
  settle: (answer: CancelAnswer | null) => void;
}

interface CancelReason {
  /** What the operator picks and what the event's reason starts with. */
  label: string;
  /** Write-ups that only name "other" need the memo to say what it was. */
  needsMemo?: boolean;
}

/**
 * The reasons CS cancels a web order for, from a year of refund notes and a sample of Gorgias cancel tickets
 * (SSD-7450). The group decides the cancel type: a customer's own reason (a change of mind) or ours.
 * Each reason is broad on purpose and the memo carries the detail, so the list stays short enough to read at a glance.
 * A customer who needs it sooner stays on their side; only our missed dispatch date is ours.
 */
const CANCEL_REASON_GROUPS: ReadonlyArray<{ type: OrderCancelType; label: string; reasons: readonly CancelReason[] }> =
  [
    {
      type: 'CHANGE_OF_MIND',
      label: "Customer's own reason",
      reasons: [
        { label: 'Changed their mind or gave no reason' },
        { label: 'Ordered by mistake: wrong item, shade, size, or twice' },
        { label: 'Will reorder: change items, address, coupon or credit' },
        { label: 'Needs it sooner, or worried about shipping cost or customs' },
        { label: 'Other customer reason', needsMemo: true },
      ],
    },
    {
      type: 'COMPANY',
      label: 'Our side',
      reasons: [
        { label: 'Out of stock, partly out of stock, or product changed' },
        { label: 'Cannot ship, or no reply to our question' },
        { label: 'We missed our promised dispatch date' },
        { label: 'Our error: price, coupon, website or duplicate payment' },
        { label: 'Suspected fraud or test order' },
        { label: 'Other reason on our side', needsMemo: true },
      ],
    },
  ];

/** Each reason keyed by group and position, so the select's value stays short and stable. */
const CANCEL_REASONS = new Map<string, CancelReason & { type: OrderCancelType }>(
  CANCEL_REASON_GROUPS.flatMap((group, g) =>
    group.reasons.map((reason, r) => [`${g}-${r}`, { ...reason, type: group.type }] as const),
  ),
);

const LONGEST_REASON = Math.max(...[...CANCEL_REASONS.values()].map((reason) => reason.label.length));
const MEMO_JOIN = ' - ';

/**
 * A locked order cancels only with a written reason (SSD-7643), whatever the cancel reason picked: the server posts it to
 * the order's comments, and refuses a change of mind after the lock without it. Any reason that is not blank will do;
 * the server caps it at 300.
 */
const OVERRIDE_MAX = 300;
const OVERRIDE_LABEL = 'Why cancel after the lock';

const CancelModal = ({
  title,
  confirmLabel,
  maxReasonLength,
  showReceivedGoods = true,
  askCancelType = false,
  lock = null,
  goodsLines = [],
  settle,
}: Pending) => {
  const [reason, setReason] = useState('');
  const [receivedGoods, setReceivedGoods] = useState<ReceivedGoods>('KEEP_IN_STOCK');
  const [reasonKey, setReasonKey] = useState('');
  const [memo, setMemo] = useState('');
  const [override, setOverride] = useState('');
  const [goodsChoices, setGoodsChoices] = useState<Partial<Record<number, CancelGoodsChoice>>>({});
  const dialogRef = useModalDialog(() => settle(null));
  const reasonId = useId();
  const memoId = useId();
  const overrideId = useId();
  const picked = askCancelType ? CANCEL_REASONS.get(reasonKey) : undefined;
  const cancelType = picked?.type ?? null;
  const locked = askCancelType && lock !== null;
  const overrideBlank = locked && override.trim() === '';
  // The picked reason leads the event's reason and the memo follows it, so the memo gets what the longest reason leaves.
  const memoMax = Math.max(0, maxReasonLength - LONGEST_REASON - MEMO_JOIN.length);
  const blocked = askCancelType
    ? picked === undefined ||
      (picked.needsMemo === true && memo.trim() === '') ||
      overrideBlank ||
      !cancelGoodsAnswered(goodsLines, goodsChoices)
    : reason.trim() === '';
  const submit = () => {
    if (blocked) {
      return;
    }
    const written = picked ? [picked.label, memo.trim()].filter((part) => part !== '').join(MEMO_JOIN) : reason.trim();
    const answer: CancelAnswer = { reason: written };
    if (showReceivedGoods) {
      answer.receivedGoods = receivedGoods;
    }
    if (cancelType !== null) {
      answer.cancelType = cancelType;
      if (locked) {
        answer.lockOverrideReason = override.trim();
      }
      if (goodsLines.length > 0) {
        answer.lines = cancelGoodsDispositions(goodsLines, goodsChoices);
      }
      const goodsNote = describeCancelGoods(goodsLines, goodsChoices);
      if (goodsNote !== '') {
        answer.goodsNote = goodsNote;
      }
    }
    settle(answer);
  };

  return (
    <dialog className="wms-prompt cancel-prompt" ref={dialogRef} aria-label={title}>
      <header>
        {title}
        <button type="button" className="x" aria-label="Close" onClick={() => settle(null)}>
          ✕
        </button>
      </header>
      <div className="body">
        {askCancelType && lock && (
          <p className="cancel-lock-note" data-testid="cancel-dialog-lock">
            <LockIcon />
            <span>{describeCancelLock(lock)}</span>
          </p>
        )}
        {askCancelType && (
          <>
            <div className="qrow cancel-reason">
              <label htmlFor={reasonId}>
                <b>Cancel reason</b>
              </label>
              <select id={reasonId} value={reasonKey} onChange={(event) => setReasonKey(event.target.value)}>
                <option value="">Select a reason</option>
                {CANCEL_REASON_GROUPS.map((group, g) => (
                  <optgroup key={group.type} label={group.label}>
                    {group.reasons.map((choice, r) => (
                      <option key={choice.label} value={`${g}-${r}`}>
                        {choice.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </>
        )}
        {askCancelType ? (
          <div className="qrow cancel-memo">
            <label htmlFor={memoId}>
              <b>Memo</b>
            </label>
            <input
              id={memoId}
              type="text"
              aria-label="Memo"
              placeholder={picked?.needsMemo ? 'Required: what the reason was' : 'Optional details'}
              value={memo}
              maxLength={memoMax}
              onChange={(event) => setMemo(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  submit();
                }
              }}
            />
          </div>
        ) : (
          <div className="qrow">
            <b>Reason</b>
            <input
              type="text"
              aria-label="Reason"
              value={reason}
              maxLength={maxReasonLength}
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  submit();
                }
              }}
            />
          </div>
        )}
        {/* Each line's goods: said for existing inventory, asked for a bought line, before the cancel can go (SSD-7643). */}
        {askCancelType && goodsLines.length > 0 && (
          <fieldset className="cancel-goods" data-testid="cancel-goods">
            <legend>What to do with the goods</legend>
            <ul>
              {goodsLines.map((line) => (
                <li key={line.lineNo} className="cancel-goods-row">
                  <span className="cancel-goods-item" title={`${line.sku} ${line.name}`}>
                    <b>{line.lineNo}</b> {line.sku} ×{line.quantity}
                    {line.name && <span className="name">{line.name}</span>}
                  </span>
                  {line.kind === 'EXISTING' && (
                    <span className="cancel-goods-fixed">
                      <b>Existing Inventory</b> · back to stock
                    </span>
                  )}
                  {line.kind === 'NOT_BOUGHT' && (
                    <span className="cancel-goods-none">Not purchased - nothing to do</span>
                  )}
                  {line.kind === 'BOUGHT' && (
                    <span className="cancel-goods-choice" role="radiogroup" aria-label={`Line ${line.lineNo} goods`}>
                      {(['RETURN', 'KEEP'] as const).map((choice) => (
                        <label key={choice} className={goodsChoices[line.lineNo] === choice ? 'is-picked' : undefined}>
                          <input
                            type="radio"
                            name={`cancel-goods-${line.lineNo}`}
                            value={choice}
                            checked={goodsChoices[line.lineNo] === choice}
                            onChange={() => setGoodsChoices((previous) => ({ ...previous, [line.lineNo]: choice }))}
                          />
                          {choice === 'RETURN'
                            ? line.arrived
                              ? 'Return'
                              : 'Return to seller'
                            : line.arrived
                              ? 'Keep in stock'
                              : 'Keep when it arrives'}
                        </label>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </fieldset>
        )}
        {/* On a locked order the box is open and required from the start, whatever reason is picked (SSD-7643). */}
        {locked && (
          <div className="cancel-override is-on" data-testid="cancel-override">
            <div className="cancel-override-head">
              <LockIcon />
              <label htmlFor={overrideId}>
                <b>{OVERRIDE_LABEL}</b>
              </label>
              <span className="cancel-override-tag">Required</span>
            </div>
            <textarea
              id={overrideId}
              aria-label={OVERRIDE_LABEL}
              rows={2}
              value={override}
              maxLength={OVERRIDE_MAX}
              aria-required="true"
              placeholder="e.g. CS confirmed the cancel to the customer before the new policy"
              onChange={(event) => setOverride(event.target.value)}
            />
            <p className="cancel-override-note" data-testid="cancel-override-note">
              Posted to the order comments when the order is cancelled.
            </p>
          </div>
        )}
        {showReceivedGoods && (
          <fieldset>
            <legend>Received goods</legend>
            <p>
              What should happen to received goods that can be written off? Goods filled from existing inventory stay in
              stock.
            </p>
            <label className="d-block">
              <input
                type="radio"
                name="receivedGoods"
                value="KEEP_IN_STOCK"
                checked={receivedGoods === 'KEEP_IN_STOCK'}
                onChange={() => setReceivedGoods('KEEP_IN_STOCK')}
              />{' '}
              Keep in stock — release the reservation; stock quantity stays unchanged.
            </label>
            <label className="d-block">
              <input
                type="radio"
                name="receivedGoods"
                value="WRITE_OFF"
                checked={receivedGoods === 'WRITE_OFF'}
                onChange={() => setReceivedGoods('WRITE_OFF')}
              />{' '}
              Write off — remove eligible received goods from stock.
            </label>
          </fieldset>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={() => settle(null)}>
          Cancel
        </button>
        <button type="button" className="btn btn-red-line btn-sm" disabled={blocked} onClick={submit}>
          {locked ? 'Cancel after the lock' : confirmLabel}
        </button>
      </div>
    </dialog>
  );
};

/**
 * The cancel dialog that asks more than a reason: the stock choice for received goods that can be written off, and
 * for an order cancel whose reason it is (SSD-7450). The ordinary one-line prompts elsewhere stay unchanged.
 */
export const useReceivedGoodsCancelDialog = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const nextId = useRef(0);
  const resolvePending = useRef<((answer: CancelAnswer | null) => void) | null>(null);
  useEffect(
    () => () => {
      resolvePending.current?.(null);
    },
    [],
  );
  const askReceivedGoods = (request: CancelRequest) =>
    new Promise<CancelAnswer | null>((resolve) => {
      // Keep state updaters pure: resolving the previous caller belongs outside React's updater.
      resolvePending.current?.(null);
      resolvePending.current = resolve;
      nextId.current += 1;
      setPending({
        ...request,
        id: nextId.current,
        settle: (answer) => {
          if (resolvePending.current !== resolve) {
            return;
          }
          resolvePending.current = null;
          setPending(null);
          resolve(answer);
        },
      });
    });
  return { askReceivedGoods, dialog: pending ? <CancelModal key={pending.id} {...pending} /> : null };
};
